/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import environmentConfig from '@/config/environment';
import { prefetchSeoMetadataInfoResults } from '@/features/common/api/getSeoMetadata';
import { getGlobalConfigFromCache, getFeatureFlag } from '@/features/globalConfig';
import { getHeaderDataFromCache } from '@/features/header/api/getHeader';
import {
  getProductDetailsURL,
  getProductReviewsURL,
  type ProductDetailsOptions,
} from '@/features/product';
import {
  prefetchProductDetails,
  getProductDetailsFromCache,
} from '@/features/product/api/getProductDetails';
import { prefetchProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import {
  ProductReviews,
  REVIEWS_DEFAULT_SORTING,
  SubmitFeedbackContextProvider,
} from '@/features/reviews';
import {
  getProductReviewsFromCache,
  prefetchProductReviews,
} from '@/features/reviews/api/getProductReviews';
import { prefetchReviewsAuthors } from '@/features/reviews/api/getReviewsAuthors';
import { type PageContextType } from '@/next-types';
import type { AppState } from '@/stores/interface';
import { configureSharedPageData } from '@/utils/configureSharedPageData.server';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { dehydrate, type QueryClient } from '@tanstack/react-query';

export default function ProductReviewsPage() {
  return (
    <SubmitFeedbackContextProvider>
      <ProductReviews />
    </SubmitFeedbackContextProvider>
  );
}

ProductReviewsPage.getInitialProps = async (ctx: PageContextType) => {
  if (__IS_SERVER__) {
    const { res, store, query } = ctx;
    const { slug } = query;

    if (!Array.isArray(slug)) {
      throw new Error('The slug for ProductDetails must be an array');
    }

    const { queryClient, initialAppState, axios } = await configureSharedPageData(ctx);

    const headerData = getHeaderDataFromCache(queryClient);
    const catalogVehicleId = headerData?.vehicleMap?.catalogVehicleId ?? '0';
    const storeNumber = headerData?.storeNumber ?? '';
    const globalConfig = getGlobalConfigFromCache(queryClient);
    const seoUrl = getProductDetailsURL(`/${slug.join('/')}`);

    const productDetailsOptions: ProductDetailsOptions = {
      vehicleId: Number(catalogVehicleId),
      locale: initialAppState.locale ?? 'en-US',
      lineCode: undefined,
      productDetailsPageUrl: seoUrl,
      userSegment: undefined,
      partNumber: undefined,
      itemIds: undefined,
      storeId: storeNumber ? Number(storeNumber) : undefined,
      preview: environmentConfig.SHOW_XM_PREVIEW_DATE,
    };

    await prefetchProductDetails(queryClient, productDetailsOptions, axios);
    const productDetails = getProductDetailsFromCache(queryClient, productDetailsOptions);

    if (productDetails?.redirectURL) {
      const targetURL =
        productDetails.redirectPageType === 'ProductDetails'
          ? getProductReviewsURL(productDetails.redirectURL)
          : productDetails.redirectURL;

      return handleRedirect(res, 301, targetURL, initialAppState, queryClient);
    }

    if (globalConfig?.PRP_ENABLED !== 'true' || !productDetails?.product?.hasReviews) {
      return handleRedirect(res, 302, '/NotFound', initialAppState, queryClient);
    }

    const { appData } = store.getState();
    const isBot = appData.deviceType === 'bot';

    const productReviewsOptions = {
      country: getCountryFromLocale(initialAppState.locale),
      customerType: 'B2C',
      skuId: productDetails?.product?.itemId ?? '',
      sortBy: REVIEWS_DEFAULT_SORTING,
      filterByStar: [],
      pageNumber: isBot ? 0 : 1,
    };

    const productReviewsPromise = prefetchProductReviews(productReviewsOptions, queryClient, axios);

    const productReviewsWithMediaPromise = prefetchProductReviews(
      {
        country: getCountryFromLocale(initialAppState.locale),
        customerType: 'B2C',
        skuId: productDetails?.product?.itemId ?? '',
        reviewsWithMedia: true,
      },
      queryClient,
      axios
    );

    const skuDetailsPromise = !!productDetails?.product?.itemId
      ? prefetchProductSkuDetails(axios, queryClient, {
          skuIds: [productDetails?.product?.itemId ?? null],
          storeNumber,
          vehicleId: catalogVehicleId !== '' ? catalogVehicleId : '0',
          isNewDealsApiEnabled: getFeatureFlag(globalConfig, 'IS_NEW_DEALS_API_ENABLED') === 'true',
        })
      : undefined;

    const promises = [productReviewsPromise, productReviewsWithMediaPromise, skuDetailsPromise];

    promises.push(
      prefetchSeoMetadataInfoResults(axios, queryClient, {
        seoUrl: `/${slug.join('/')}`,
        country: getCountryFromLocale(initialAppState?.locale),
        customerType: 'B2C',
        preview: environmentConfig.SHOW_XM_PREVIEW_DATE,
        pageType: 'ProductReview',
      })
    );

    await Promise.all(promises);

    if (isBot && globalConfig?.BV_API_ENABLED === 'true') {
      const productReviews = getProductReviewsFromCache(productReviewsOptions, queryClient);
      try {
        await prefetchReviewsAuthors(
          {
            profileIDs: (
              (productReviews?.reviews ?? [])
                .map((review) => review.profileId)
                .filter((id) => id) as string[]
            ).sort(),
            stats: ['Reviews'],
          },
          queryClient,
          axios
        );
      } catch {}
    }

    return {
      initialAppState,
      dehydratedState: dehydrate(queryClient),
    };
  }

  return {};
};

function handleRedirect(
  res: PageContextType['res'],
  status: number,
  location: string,
  initialAppState: AppState,
  queryClient: QueryClient
) {
  res
    ?.writeHead(status, {
      Location: location,
    })
    .end();

  return {
    initialAppState,
    dehydratedState: dehydrate(queryClient),
  };
}
